import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Minus, Plus, Star, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useStore } from "../lib/store";
import { money } from "../lib/api";
import { ProductCard, Badge } from "../components/site/ProductCard";
import { SectionTitle } from "../components/site/Reveal";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { byId, products, loading, addToCart, toggleWish, isWished } = useStore();
  const product = byId[id];
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [spot, setSpot] = useState({ x: 50, y: 50 });

  if (loading) return <p className="py-40 text-center font-display text-2xl italic text-plum/60">Loading…</p>;
  if (!product) {
    return (
      <main className="py-40 text-center" data-testid="product-not-found">
        <p className="font-script text-6xl">Oh darling, it's gone</p>
        <Link to="/shop" className="btn-luxe mt-8">Back to shop</Link>
      </main>
    );
  }

  const sizes = product.sizes ?? [];
  const colors = product.colors ?? [];
  const soldOut = product.stock === 0;
  const maxQty = soldOut ? 1 : Math.max(1, product.stock ?? 20);
  const chosenSize = size ?? (sizes.length ? null : null);
  const needsSize = sizes.length > 0 && !chosenSize;
  const add = () => addToCart(product, { size: chosenSize, color: color ?? colors[0] ?? null, quantity: qty });
  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);
  const wished = isWished(product.id);

  return (
    <main data-testid="product-detail-page" className="pb-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 pt-8">
        <nav className="text-xs uppercase tracking-[0.2em] text-plum/65">
          <Link to="/shop" className="hover:text-magenta">Shop</Link> / <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-magenta">{product.category}</Link>
        </nav>
        <div className="mt-8 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0 round 2rem)" }}
            animate={{ clipPath: "inset(0 0 0% 0 round 2rem)" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setSpot({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); }}
            className="relative aspect-[4/5] overflow-hidden rounded-[2rem] rounded-tr-[6rem] bg-cream-warm"
          >
            <Badge label={product.badge} />
            <img data-testid="product-detail-image" src={product.image} alt={product.name} className="h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-0 transition-[background] duration-200" style={{ background: `radial-gradient(circle at ${spot.x}% ${spot.y}%, rgba(255,255,255,.18), transparent 35%, rgba(42,15,46,.28) 90%)` }} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="lg:pt-6">
            <p className="eyebrow text-gold-deep">{product.category}</p>
            <h1 data-testid="product-detail-name" className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{product.name}</h1>
            <div className="mt-4 flex items-center gap-2 text-sm text-plum/75">
              {[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`h-4 w-4 ${s <= Math.round(product.rating) ? "fill-gold text-gold" : "text-plum/25"}`} />)}
              <span>{product.rating} rating</span>
            </div>
            <div className="mt-6 flex items-baseline gap-3">
              <p data-testid="product-detail-price" className="font-display text-3xl">{money(product.price)}</p>
              {product.compare_at && <p className="text-lg text-plum/50 line-through">{money(product.compare_at)}</p>}
              {product.stock === 0 ? (
                <span data-testid="product-detail-sold-out" className="rounded-full bg-plum px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-cream">Sold out</span>
              ) : product.stock <= 5 && (
                <span data-testid="product-detail-low-stock" className="text-xs font-semibold text-magenta">Only {product.stock} left</span>
              )}
            </div>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-plum/80">{product.description}</p>

            {colors.length > 0 && (
              <div className="mt-8">
                <p className="eyebrow text-plum/70">Color</p>
                <div className="mt-3 flex gap-3">
                  {colors.map((c) => (
                    <button key={c} aria-label={`Color ${c}`} data-testid={`product-color-${c.replace("#", "")}`} onClick={() => setColor(c)} className={`h-9 w-9 rounded-full border border-plum/20 transition-transform hover:scale-110 ${(color ?? colors[0]) === c ? "ring-2 ring-magenta ring-offset-2 ring-offset-cream" : ""}`} style={{ background: c }} />
                  ))}
                </div>
              </div>
            )}
            {sizes.length > 0 && (
              <div className="mt-8">
                <p className="eyebrow text-plum/70">Size</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {sizes.map((s) => (
                    <button key={s} data-testid={`product-size-${s.toLowerCase()}`} onClick={() => setSize(s)} className={`h-11 min-w-[3rem] rounded-full border px-4 text-sm font-semibold transition-colors ${size === s ? "border-plum bg-plum text-cream" : "border-plum/20 hover:border-plum"}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="flex items-center rounded-full border border-plum/20">
                <button aria-label="Decrease quantity" data-testid="product-qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-12 w-12 place-items-center"><Minus className="h-4 w-4" /></button>
                <span data-testid="product-qty-value" className="w-8 text-center font-semibold">{qty}</span>
                <button aria-label="Increase quantity" data-testid="product-qty-increase" onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="grid h-12 w-12 place-items-center"><Plus className="h-4 w-4" /></button>
              </div>
              <button data-testid="product-add-to-cart-button" disabled={needsSize || soldOut} onClick={add} className="btn-luxe flex-1 disabled:cursor-not-allowed disabled:opacity-50">{soldOut ? "Sold Out" : needsSize ? "Select a size" : "Add to Cart"}</button>
              <button aria-label="Toggle wishlist" data-testid="product-wishlist-button" onClick={() => toggleWish(product)} className="grid h-12 w-12 place-items-center rounded-full border border-plum/20 transition-colors hover:border-magenta">
                <Heart className={`h-5 w-5 ${wished ? "fill-magenta text-magenta" : ""}`} />
              </button>
            </div>
            <button data-testid="product-buy-now-button" disabled={needsSize || soldOut} onClick={() => { add(); navigate("/cart"); }} className="btn-ghost mt-3 w-full disabled:opacity-50">Buy it now</button>

            <div className="mt-8 grid grid-cols-3 gap-3 text-center text-[11px] uppercase tracking-[0.12em] text-plum/75">
              {[[Truck, "Free over $75"], [RotateCcw, "Easy returns"], [ShieldCheck, "Secure checkout"]].map(([I, l]) => (
                <div key={l} className="rounded-2xl bg-white py-4"><I className="mx-auto mb-2 h-5 w-5 text-magenta" strokeWidth={1.5} />{l}</div>
              ))}
            </div>

            <Accordion type="single" collapsible className="mt-8">
              <AccordionItem value="details"><AccordionTrigger data-testid="product-accordion-details" className="font-display text-lg">Details</AccordionTrigger><AccordionContent className="text-plum/80">{product.description} Thoughtfully selected by the Luxe Dream Market team.</AccordionContent></AccordionItem>
              <AccordionItem value="shipping"><AccordionTrigger data-testid="product-accordion-shipping" className="font-display text-lg">Shipping</AccordionTrigger><AccordionContent className="text-plum/80">Please allow 1–3 business days for order processing before your order ships. Estimated delivery is 3–7 business days after that. Orders of $75+ ship free; other shipping charges are displayed at checkout. You'll receive a shipping confirmation with tracking information when available.</AccordionContent></AccordionItem>
              <AccordionItem value="returns"><AccordionTrigger data-testid="product-accordion-returns" className="font-display text-lg">Returns</AccordionTrigger><AccordionContent className="text-plum/80">Eligible items may be returned within 14 days of delivery — unused, unworn, unwashed, and in their original condition with tags. Contact us through the site before sending anything back; approved refunds go to your original payment method.</AccordionContent></AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mx-auto mt-24 max-w-7xl px-4 sm:px-8">
          <SectionTitle script="You May Also Love" testId="related-heading" />
          <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </main>
  );
}
